import { Chart, registerables } from 'chart.js';
import { api } from '../api.js';
import { router } from '../router.js';
import { showNotification } from '../components/notification.js';
import { FEVER_COLORS, FEVER_BG } from '../utils/colors.js';

Chart.register(...registerables);

/**
 * Custom Chart.js plugin: paints the green/yellow/red CCPM fever zones as
 * triangular regions in chart space. The zone boundaries are the lines
 * y=x/3 (green→yellow) and y=2x/3 (yellow→red), both passing through the
 * origin. We use the chart's scale converters so the zones stay aligned
 * regardless of axis range.
 */
const feverZonesPlugin = {
  id: 'feverZones',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales: { x, y } } = chart;
    if (!chartArea) return;
    const xMax = x.max;
    const yMax = y.max;
    const px = (vx, vy) => ({ X: x.getPixelForValue(vx), Y: y.getPixelForValue(vy) });

    const fillTriangle = (pts, color) => {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(pts[0].X, pts[0].Y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].X, pts[i].Y);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
    };

    // Green zone: y < x/3 — triangle (0,0) → (xMax, 0) → (xMax, xMax/3)
    fillTriangle(
      [px(0, 0), px(xMax, 0), px(xMax, xMax / 3)],
      FEVER_BG.green,
    );
    // Yellow zone: x/3 ≤ y < 2x/3 — triangle (0,0) → (xMax, xMax/3) → (xMax, 2*xMax/3)
    fillTriangle(
      [px(0, 0), px(xMax, xMax / 3), px(xMax, (2 * xMax) / 3)],
      FEVER_BG.yellow,
    );
    // Red zone: y ≥ 2x/3 — quadrilateral (0,0) → (xMax, 2*xMax/3) → (xMax, yMax) → (0, yMax)
    fillTriangle(
      [px(0, 0), px(xMax, (2 * xMax) / 3), px(xMax, yMax), px(0, yMax)],
      FEVER_BG.red,
    );

    // Boundary dashed lines for clarity on dark themes.
    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1;
    [
      { color: FEVER_COLORS.yellow, end: xMax / 3 },
      { color: FEVER_COLORS.red,    end: (2 * xMax) / 3 },
    ].forEach(({ color, end }) => {
      const a = px(0, 0), b = px(xMax, end);
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.moveTo(a.X, a.Y);
      ctx.lineTo(b.X, b.Y);
      ctx.stroke();
    });
    ctx.restore();
  },
};
Chart.register(feverZonesPlugin);

let _chart = null;

export async function initFever(container) {
  container.style.cssText = 'height:100%; display:flex; flex-direction:column; overflow:hidden;';
  container.innerHTML = `
    <div class="gantt-toolbar fever-toolbar">
      <span class="toolbar-label">Fever Chart 2D — прогресс vs потребление буфера</span>
      <label class="fever-toggle" style="margin-left: var(--space-4);">
        <input type="checkbox" class="fever-trajectories" /> Показать траектории
      </label>
      <button class="fever-snapshot scale-btn" type="button" style="margin-left: auto;">
        💾 Сохранить snapshot
      </button>
      <span class="saving-indicator" hidden>Загрузка…</span>
    </div>
    <div class="fever-chart-host" style="flex:1; min-height:0; padding: var(--space-3) var(--space-4);">
      <canvas id="fever-canvas"></canvas>
    </div>
    <div class="fever-empty" hidden>
      <p>Нет активных проектов для отображения.</p>
    </div>
  `;

  const trajCheckbox = container.querySelector('.fever-trajectories');
  const snapshotBtn  = container.querySelector('.fever-snapshot');
  const savingEl     = container.querySelector('.saving-indicator');
  const emptyEl      = container.querySelector('.fever-empty');

  let projects = [];
  let trajectoriesByProject = new Map(); // project_id → [{x, y}, ...]

  async function fetchAll() {
    savingEl.hidden = false;
    try {
      projects = await api.stats.feverChart();
    } catch (e) {
      if (e.message === '401') return;
      showNotification('Не удалось загрузить fever-chart: ' + e.message, 'error');
      return;
    } finally {
      savingEl.hidden = true;
    }
    if (!projects.length) {
      emptyEl.hidden = false;
      return;
    }
    emptyEl.hidden = true;

    if (trajCheckbox.checked) {
      await fetchTrajectories();
    } else {
      trajectoriesByProject.clear();
    }
    render();
  }

  async function fetchTrajectories() {
    savingEl.hidden = false;
    try {
      const results = await Promise.all(
        projects.map(p =>
          api.bufferHistory(p.project_id, 30).then(rows => [p.project_id, rows]).catch(() => [p.project_id, []])
        )
      );
      trajectoriesByProject = new Map(results);
    } catch (e) {
      console.warn('Trajectories fetch failed:', e.message);
    } finally {
      savingEl.hidden = true;
    }
  }

  function render() {
    const datasets = buildDatasets(projects, trajectoriesByProject);
    if (_chart) _chart.destroy();
    _chart = new Chart(document.getElementById('fever-canvas'), {
      type: 'scatter',
      data: { datasets },
      options: chartOptions(projects),
    });
  }

  trajCheckbox.addEventListener('change', async () => {
    if (trajCheckbox.checked) await fetchTrajectories();
    else trajectoriesByProject.clear();
    render();
  });

  snapshotBtn.addEventListener('click', async () => {
    snapshotBtn.disabled = true;
    try {
      const r = await api.snapshotBufferState();
      showNotification(`Snapshot сохранён (${r.created} проектов)`, 'success');
      // Re-fetch trajectories so the new point shows up.
      if (trajCheckbox.checked) {
        await fetchTrajectories();
        render();
      }
    } catch (e) {
      showNotification('Snapshot не удался: ' + e.message, 'error');
    } finally {
      snapshotBtn.disabled = false;
    }
  });

  await fetchAll();
}

// ── Chart construction ──────────────────────────────────────────────────────

function buildDatasets(projects, trajectoriesByProject) {
  const datasets = [];

  // Current-state scatter — one dataset that holds all project points so we
  // can colour-code by fever zone and drive a single tooltip path.
  const currentPoints = projects.map(p => ({
    x: numOrNull(p.progress_pct),
    y: numOrNull(p.buffer_consumed_pct),
    r: pointRadius(p),
    project_id: p.project_id,
    project_name: p.project_name,
    fever_zone: p.fever_zone,
  })).filter(pt => pt.x != null && pt.y != null);

  datasets.push({
    type: 'scatter',
    label: 'Текущее состояние',
    data: currentPoints,
    backgroundColor: (ctx) => FEVER_COLORS[ctx.raw?.fever_zone] ?? '#888',
    borderColor: '#0d1117',
    borderWidth: 1,
    pointRadius: (ctx) => ctx.raw?.r ?? 6,
    pointHoverRadius: (ctx) => (ctx.raw?.r ?? 6) + 2,
  });

  // Trajectory line per project (only those with ≥2 historical points).
  for (const [projectId, history] of trajectoriesByProject) {
    if (!history || history.length < 2) continue;
    const proj = projects.find(p => p.project_id === projectId);
    if (!proj) continue;
    datasets.push({
      type: 'line',
      label: `↳ ${proj.project_name}`,
      data: history.map(h => ({
        x: numOrNull(h.progress_pct) ?? 0,
        y: numOrNull(h.buffer_consumed_pct) ?? 0,
      })),
      borderColor: FEVER_COLORS[proj.fever_zone] ?? '#888',
      borderWidth: 1,
      borderDash: [4, 4],
      pointRadius: 2,
      fill: false,
      tension: 0,
      showLine: true,
    });
  }

  return datasets;
}

function chartOptions(projects) {
  const yMax = Math.max(
    100,
    ...projects.map(p => Number(p.buffer_consumed_pct) || 0),
  ) + 10;

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 200 },
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const r = ctx.raw;
            if (r?.project_name) {
              return [
                r.project_name,
                `Прогресс: ${(r.x ?? 0).toFixed(1)}%`,
                `Буфер съеден: ${(r.y ?? 0).toFixed(1)}%`,
                `Зона: ${r.fever_zone}`,
              ];
            }
            return null;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        min: 0, max: 100,
        ticks: { color: '#8b949e', callback: v => v + '%' },
        grid:  { color: 'rgba(139,148,158,0.10)' },
        title: { display: true, text: 'Прогресс (% выполненной критической цепи)', color: '#8b949e' },
      },
      y: {
        type: 'linear',
        min: 0, max: yMax,
        ticks: { color: '#8b949e', callback: v => v + '%' },
        grid:  { color: 'rgba(139,148,158,0.10)' },
        title: { display: true, text: 'Буфер съеден (%)', color: '#8b949e' },
      },
    },
    onClick: (_evt, elements) => {
      if (!elements.length) return;
      const el = elements[0];
      const point = _chart.data.datasets[el.datasetIndex]?.data[el.index];
      if (point?.project_id) router.push('/gantt/' + point.project_id);
    },
  };
}

function numOrNull(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pointRadius(project) {
  // Sqrt-scale by critical_chain_minutes so very large projects don't dwarf
  // small ones. 4 is the floor for visibility, 22 the ceiling.
  const ccm = Math.max(0, Number(project.critical_chain_minutes ?? project.critical_chain_min ?? 0));
  if (!ccm) return 6;
  // Map 0..30000 minutes → 4..22 radius.
  return Math.max(4, Math.min(22, Math.sqrt(ccm) / 8 + 4));
}
